/**
 * Aspect Admin — Integrations / API keys / webhooks.
 */

import { icon } from "./icons.js";
import { renderShell, pageHead, statusBadge, toast, openModal, closeOverlay } from "./shell.js";
import { escapeHtml } from "./auth_shell.js";
import { getSession } from "../auth_store.js";
import { navigate } from "../router.js";
import { t } from "../i18n.js";
import { INTEGRATIONS, API_KEYS, WEBHOOKS } from "../mock_data.js";

let state = { tab: "apps" };

function appsHtml() {
  return `
    <div class="ap-grid ap-grid--3">
      ${INTEGRATIONS.map(
        (app) => `
        <div class="ap-panel ap-panel--hover ap-intcard">
          <div class="ap-intcard__head">
            <span class="ap-intcard__logo">${app.initial}</span>
            <div>
              <h3 class="ap-intcard__name">${escapeHtml(app.name)}</h3>
              ${app.connected ? `<span class="ap-badge ap-badge--ok" style="margin-top:0.2rem;"><span class="ap-dot"></span>${t("Connected")}</span>` : `<span class="ap-badge ap-badge--mute">${t("Not connected")}</span>`}
            </div>
          </div>
          <p class="ap-intcard__desc">${t(app.desc)}</p>
          <div class="ap-intcard__foot">
            ${
              app.connected
                ? `<button type="button" class="ap-btn ap-btn--ghost ap-btn--sm" data-app="${app.id}" data-act="configure">${icon("sliders", 14)} ${t("Configure")}</button>
                   <button type="button" class="ap-btn ap-btn--ghost ap-btn--sm" data-app="${app.id}" data-act="disconnect">${t("Disconnect")}</button>`
                : `<button type="button" class="ap-btn ap-btn--soft ap-btn--sm" data-app="${app.id}" data-act="connect">${icon("plug", 14)} ${t("Connect")}</button>`
            }
          </div>
        </div>`
      ).join("")}
    </div>
  `;
}

function keysHtml() {
  return `
    <div class="ap-stack">
      <div class="ap-panel">
        <div class="ap-panel__head">
          <div>
            <h3 class="ap-panel__title">${t("API keys")}</h3>
            <p class="ap-panel__sub">${t("Authenticate server-to-server requests against the Aspect API")}</p>
          </div>
          <button type="button" class="ap-btn ap-btn--primary ap-btn--sm" id="ik-new">${icon("key", 14)} ${t("Generate key")}</button>
        </div>
        <div class="ap-tablewrap ap-scroll">
          <table class="ap-table">
            <thead><tr><th>${t("Key")}</th><th>${t("Token")}</th><th>${t("Scopes")}</th><th>${t("Created")}</th><th>${t("Last used")}</th><th></th></tr></thead>
            <tbody>
              ${API_KEYS.map(
                (k) => `
                <tr>
                  <td class="ap-cell-main">${t(k.label)}</td>
                  <td><span class="ap-codechip">${k.masked}<button type="button" class="ap-iconbtn ap-iconbtn--bare" data-key-copy="${k.id}" style="width:1.4rem;height:1.4rem;" title="${t("Copy")}">${icon("copy", 12)}</button></span></td>
                  <td><span class="ap-badge ap-badge--mute">${t(k.scopes)}</span></td>
                  <td>${k.created}</td>
                  <td>${t(k.lastUsed)}</td>
                  <td class="ap-cell-actions">
                    <button type="button" class="ap-btn ap-btn--ghost ap-btn--sm" data-key-rotate="${k.id}">${icon("refresh", 13)} ${t("Rotate")}</button>
                    <button type="button" class="ap-iconbtn ap-iconbtn--bare" data-key-revoke="${k.id}" title="${t("Revoke")}">${icon("trash", 15)}</button>
                  </td>
                </tr>`
              ).join("")}
            </tbody>
          </table>
        </div>
        <div class="ap-panel__foot">
          <span>${t("Keys grant programmatic access — store them in a secrets manager, never in code")}</span>
          <a class="ap-btn ap-btn--ghost ap-btn--sm" href="#/support">${t("API docs")} ${icon("external", 12)}</a>
        </div>
      </div>
    </div>
  `;
}

function webhooksHtml() {
  return `
    <div class="ap-panel">
      <div class="ap-panel__head">
        <div>
          <h3 class="ap-panel__title">${t("Webhook endpoints")}</h3>
          <p class="ap-panel__sub">${t("Receive real-time events when jobs finish, tours publish, or spaces change")}</p>
        </div>
        <button type="button" class="ap-btn ap-btn--primary ap-btn--sm" id="wh-new">${icon("plus", 14)} ${t("Add endpoint")}</button>
      </div>
      <div class="ap-tablewrap ap-scroll">
        <table class="ap-table">
          <thead><tr><th>${t("Endpoint")}</th><th>${t("Events")}</th><th>${t("Status")}</th><th class="ap-cell-num">${t("Delivery rate")}</th><th></th></tr></thead>
          <tbody>
            ${WEBHOOKS.map(
              (w) => `
              <tr>
                <td><span class="ap-mono" style="color:var(--ap-ink);">${escapeHtml(w.url)}</span></td>
                <td>${w.events.split(", ").map((ev) => `<span class="ap-codechip" style="padding:0.12rem 0.45rem;margin-right:0.25rem;">${ev}</span>`).join("")}</td>
                <td>${statusBadge(w.status)}</td>
                <td class="ap-cell-num"><span class="ap-badge ${w.success >= 99 ? "ap-badge--ok" : w.success >= 95 ? "ap-badge--cyan" : "ap-badge--warn"}">${w.success}%</span></td>
                <td class="ap-cell-actions">
                  <button type="button" class="ap-btn ap-btn--ghost ap-btn--sm" data-wh-test="${w.id}">${icon("send", 13)} ${t("Test")}</button>
                  <button type="button" class="ap-iconbtn ap-iconbtn--bare" data-wh-del="${w.id}" title="${t("Delete")}">${icon("trash", 15)}</button>
                </td>
              </tr>`
            ).join("")}
          </tbody>
        </table>
      </div>
      <div class="ap-panel__foot">
        <span>${t("Deliveries are signed with your webhook secret · retried 5× with backoff")}</span>
        <button type="button" class="ap-btn ap-btn--ghost ap-btn--sm" id="wh-secret">${icon("eye", 13)} ${t("Reveal signing secret")}</button>
      </div>
    </div>
  `;
}

function newKeyModal(root) {
  const panel = openModal(root, {
    title: t("Generate API key"),
    bodyHtml: `
      <div class="ap-field"><label class="ap-field__label">${t("Key label")}</label><input class="ap-input" placeholder="${t("e.g. CI pipeline")}" /></div>
      <div class="ap-field">
        <label class="ap-field__label">${t("Scopes")}</label>
        <div class="ap-stack" style="gap:0.5rem;">
          ${["Read spaces & tours", "Create AI jobs", "Manage webhooks", "Full admin access"]
            .map(
              (s, i) => `
            <label class="ap-row" style="gap:0.6rem;cursor:pointer;">
              <span class="ap-switch"><input type="checkbox" ${i < 2 ? "checked" : ""} /><span class="ap-switch__track"></span></span>
              <span style="font-size:0.82rem;color:var(--ap-slate);">${t(s)}</span>
            </label>`
            )
            .join("")}
        </div>
      </div>
      <p class="ap-note" style="margin:0;">${t("The full token is shown once after generation. Store it securely.")}</p>
    `,
    footHtml: `
      <button type="button" class="ap-btn ap-btn--ghost" data-mclose>${t("Cancel")}</button>
      <button type="button" class="ap-btn ap-btn--primary" data-mgen>${icon("key", 15)} ${t("Generate")}</button>
    `,
  });
  panel?.querySelector("[data-mclose]")?.addEventListener("click", () => closeOverlay(root));
  panel?.querySelector("[data-mgen]")?.addEventListener("click", () => {
    closeOverlay(root);
    toast(root, t("Key generated — copied to clipboard"), "ok");
  });
}

export function renderIntegrations(root) {
  const session = getSession();
  if (!session) { navigate("/login", true); return; }

  const content = `
    ${pageHead(t("Integrations"), t("Connected apps, API access, and event webhooks."), `
      <a class="ap-btn" href="#/support">${icon("doc", 15)} ${t("API documentation")}</a>
    `)}

    <div class="ap-toolbar">
      <div class="ap-seg">
        <button type="button" class="ap-seg__btn ${state.tab === "apps" ? "ap-seg__btn--on" : ""}" data-tab="apps">${icon("plug", 14)} ${t("Apps")}</button>
        <button type="button" class="ap-seg__btn ${state.tab === "keys" ? "ap-seg__btn--on" : ""}" data-tab="keys">${icon("key", 14)} ${t("API keys")}</button>
        <button type="button" class="ap-seg__btn ${state.tab === "webhooks" ? "ap-seg__btn--on" : ""}" data-tab="webhooks">${icon("webhook", 14)} ${t("Webhooks")}</button>
      </div>
    </div>

    ${state.tab === "apps" ? appsHtml() : state.tab === "keys" ? keysHtml() : webhooksHtml()}
  `;

  const el = renderShell(root, "/integrations", content);
  if (!el) return;

  el.querySelectorAll("[data-tab]").forEach((tab) =>
    tab.addEventListener("click", () => {
      state = { tab: tab.dataset.tab };
      renderIntegrations(root);
    })
  );

  el.addEventListener("click", (e) => {
    const appBtn = e.target.closest("[data-app]");
    if (appBtn) {
      const app = INTEGRATIONS.find((a) => a.id === appBtn.dataset.app);
      const act = appBtn.dataset.act;
      if (act === "connect") toast(root, t("Redirecting to {n} OAuth…", { n: app.name }), "info");
      if (act === "disconnect") toast(root, t("{n} disconnected", { n: app.name }), "info");
      if (act === "configure") toast(root, t("{n} settings opening…", { n: app.name }), "info");
      return;
    }
    if (e.target.closest("[data-key-copy]")) { toast(root, t("Key prefix copied"), "ok"); return; }
    if (e.target.closest("[data-key-rotate]")) { toast(root, t("Key rotated — update your services"), "ok"); return; }
    if (e.target.closest("[data-key-revoke]")) { toast(root, t("Key revoked immediately"), "info"); return; }
    if (e.target.closest("[data-wh-test]")) { toast(root, t("Test event delivered · 200 OK in 184ms"), "ok"); return; }
    if (e.target.closest("[data-wh-del]")) { toast(root, t("Webhook endpoint deleted"), "info"); return; }
  });

  el.querySelector("#ik-new")?.addEventListener("click", () => newKeyModal(root));
  el.querySelector("#wh-new")?.addEventListener("click", () => toast(root, t("Enter an HTTPS endpoint to receive events"), "info"));
  el.querySelector("#wh-secret")?.addEventListener("click", () => toast(root, t("Re-authenticate to reveal the signing secret"), "info"));
}
